'use client'

import { ChevronDown, Plus, RefreshCw, Target, Trash2 } from 'lucide-react'
import type { Sprint } from '@/lib/types'
import type { Health } from '@/lib/dev-sprint'
import { isoDay } from '@/lib/dev-sprint'
import { HealthBadge } from './ui'

const fmt = (iso: string) =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '—'

interface Props {
  sprints: Sprint[]
  sprint: Sprint | null
  health: Health
  daysLeft: number | null
  isLead: boolean
  readOnly: boolean
  onSelect: (id: string) => void
  onRefresh: () => void
  onNewSprint: () => void
  onNewTask: () => void
  onDeleteSprint: (id: string) => void
}

export function SprintTopBar({
  sprints, sprint, health, daysLeft, isLead, readOnly, onSelect, onRefresh, onNewSprint, onNewTask, onDeleteSprint,
}: Props) {
  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4 flex flex-wrap items-center gap-x-5 gap-y-3">
      {/* Sprint selector */}
      <div className="relative">
        <select
          value={sprint?.id ?? ''}
          onChange={(e) => onSelect(e.target.value)}
          disabled={sprints.length === 0}
          className="appearance-none bg-[#F7F6F2] border border-[#E7E5DF] rounded-xl pl-3 pr-8 py-2 text-[14px] font-bold text-[#1A1916] focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40 disabled:opacity-50 cursor-pointer"
        >
          {sprints.length === 0 && <option value="">Belum ada sprint</option>}
          {sprints.map((s) => (
            <option key={s.id} value={s.id}>{s.name} · {fmt(isoDay(s.startDate))}–{fmt(isoDay(s.endDate))}</option>
          ))}
        </select>
        <ChevronDown className="w-4 h-4 text-[#8B8A85] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {sprint && (
        <>
          <div className="flex items-center gap-2 min-w-0">
            <Target className="w-4 h-4 text-[#D4A843] flex-shrink-0" />
            <span className="text-[13px] text-[#4A4844] truncate max-w-[280px]">
              {sprint.goal || <span className="italic text-[#AEADA8]">Tanpa goal</span>}
            </span>
          </div>

          <div className="text-[13px] text-[#8B8A85] tabular-nums">
            {fmt(isoDay(sprint.startDate))} – {fmt(isoDay(sprint.endDate))}
          </div>

          {daysLeft !== null && (
            <span
              className="text-[12px] font-semibold px-2 py-1 rounded-lg tabular-nums"
              style={
                daysLeft < 0
                  ? { backgroundColor: '#FCE8E8', color: '#C0362C' }
                  : daysLeft <= 3
                  ? { backgroundColor: '#FFF4DC', color: '#9A6B00' }
                  : { backgroundColor: '#F2F1EC', color: '#6B6A65' }
              }
            >
              {daysLeft < 0 ? `Lewat ${-daysLeft} hari` : daysLeft === 0 ? 'Berakhir hari ini' : `${daysLeft} hari lagi`}
            </span>
          )}

          <HealthBadge health={health} />
        </>
      )}

      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[13px] font-medium text-[#6B6A65] hover:bg-[#F2F1EC] transition-colors"
          title="Muat ulang"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        {isLead && !readOnly && sprint && (
          <button
            onClick={() => {
              if (window.confirm(`Hapus sprint "${sprint.name}"? Task tetap ada, hanya lepas dari sprint ini.`)) {
                onDeleteSprint(sprint.id)
              }
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[13px] font-medium text-[#B4453C] hover:bg-[#FCE8E8] transition-colors"
            title="Hapus sprint"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {isLead && !readOnly && (
          <>
            <button
              onClick={onNewSprint}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-[#1A1916] bg-[#F2F1EC] hover:bg-[#E9E7E0] transition-colors"
            >
              <Plus className="w-4 h-4" /> Sprint
            </button>
            <button
              onClick={onNewTask}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#1A1916] hover:bg-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Task
            </button>
          </>
        )}
      </div>
    </div>
  )
}
